// Phase 1.9 — JWT-based AuthProvider that talks to al-bayan-backend.
//
// Selected by `AuthContext.js` when REACT_APP_AUTH_BACKEND=api. Exports the
// SAME value-object shape as the Firebase impl so every `useAuth()` consumer
// keeps working unchanged (see docs/migration-auth-surface.md).
//
// Auth flow:
//   1. Page load → POST /api/auth/refresh (uses httpOnly cookie). If 200,
//      we have an accessToken + user; else currentUser stays null.
//   2. login → POST /api/auth/login → store accessToken in memory, set user.
//   3. logout → POST /api/auth/logout → clear state + cookie.
//
// The accessToken lives in a module-level variable (NOT localStorage) and is
// re-attached to every API call via the `apiClient` helper. Refresh-on-401
// retry is the apiClient's job, not this provider's.

import React, { useEffect, useState, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { setCurrentTenantId } from '../services/tenantContext';
import { ROLES, resolveRole } from '../services/userService';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4001';

// Module-level access-token cache. Other API helpers (Phase 2+) read this
// via getAccessToken() to authorize their requests.
let _accessToken = null;
const _listeners = new Set();

export const getAccessToken = () => _accessToken;
export const setAccessToken = (token) => {
    _accessToken = token;
    _listeners.forEach((fn) => fn(token));
};
export const onAccessTokenChange = (fn) => {
    _listeners.add(fn);
    return () => _listeners.delete(fn);
};

const apiFetch = async (path, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (_accessToken) headers.Authorization = `Bearer ${_accessToken}`;
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
        credentials: 'include'
    });
    let body = null;
    try {
        body = await res.json();
    } catch {
        // non-JSON body
    }
    if (!res.ok) {
        const err = new Error(body?.error || `Request failed: ${res.status}`);
        err.status = res.status;
        err.body = body;
        throw err;
    }
    return body;
};

// Adapt the backend `user` payload to the shape AuthContext consumers expect
// (see docs/migration-auth-surface.md). `currentUser` mirrors a Firebase User
// surface (uid/email/displayName/phoneNumber); `userProfile` mirrors the
// `users/{uid}` Firestore doc.
const adaptUser = (user) => {
    if (!user) return { currentUser: null, userProfile: null };
    return {
        currentUser: {
            uid: user.id || user.uid,
            email: user.email || null,
            displayName: user.displayName || null,
            phoneNumber: user.phoneNumber || null,
            photoURL: user.avatarUrl || null,
            emailVerified: !!user.emailVerified
        },
        userProfile: {
            uid: user.id || user.uid,
            id: user.id || user.uid,
            role: user.role || 'teacher',
            tenantId: user.tenantId || null,
            displayName: user.displayName || null,
            email: user.email || null,
            phone: user.phoneNumber || null,
            linkedStudentIds: user.linkedStudentIds || [],
            studentId: user.studentId || null,
            avatarUrl: user.avatarUrl || null,
            mustResetPassword: !!user.mustResetPassword,
            customClaims: user.customClaims || null
        }
    };
};

const notImplemented = (name) => () => {
    throw new Error(
        `${name} is not available on the API auth backend. ` +
        `Set REACT_APP_AUTH_BACKEND=firebase to use it during the transition.`
    );
};

export const ApiAuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);

    const applyUser = useCallback((user) => {
        const adapted = adaptUser(user);
        setCurrentUser(adapted.currentUser);
        setUserProfile(adapted.userProfile);
        setCurrentTenantId(adapted.userProfile?.tenantId || null);
    }, []);

    // On mount, try to restore session via httpOnly refresh cookie.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await apiFetch('/api/auth/refresh', { method: 'POST' });
                if (cancelled) return;
                setAccessToken(data.accessToken);
                applyUser(data.user);
            } catch {
                if (cancelled) return;
                setAccessToken(null);
                applyUser(null);
            } finally {
                if (!cancelled) setAuthReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [applyUser]);

    const login = useCallback(async (email, password) => {
        const data = await apiFetch('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password })
        });
        setAccessToken(data.accessToken);
        applyUser(data.user);
        // Mirror the Firebase UserCredential shape consumers read.
        return { user: { uid: data.user.id, email: data.user.email, displayName: data.user.displayName } };
    }, [applyUser]);

    const register = useCallback(async ({ email, password, displayName, role = ROLES.PARENT }) => {
        const data = await apiFetch('/api/auth/signup', {
            method: 'POST',
            body: JSON.stringify({ email, password, displayName, role })
        });
        // Signup does NOT log the user in — they must verify their email first
        // (matches Firebase behaviour). Caller shows the "check your inbox" UI.
        return { user: { uid: data.user.id, email: data.user.email, displayName: data.user.displayName } };
    }, []);

    const logout = useCallback(async () => {
        try {
            await apiFetch('/api/auth/logout', { method: 'POST' });
        } catch {
            // Logout endpoint always returns 200 in practice; swallow.
        }
        setAccessToken(null);
        applyUser(null);
    }, [applyUser]);

    const resetPassword = useCallback(async (email) => {
        await apiFetch('/api/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    }, []);

    const refreshProfile = useCallback(async () => {
        if (!_accessToken) return;
        setProfileLoading(true);
        try {
            const data = await apiFetch('/api/auth/me', { method: 'GET' });
            applyUser(data.user);
        } catch {
            // Token expired or invalid — clear state.
            setAccessToken(null);
            applyUser(null);
        } finally {
            setProfileLoading(false);
        }
    }, [applyUser]);

    const role = resolveRole(userProfile?.role);
    const linkedStudentIds = userProfile?.linkedStudentIds || [];
    const tenantId = userProfile?.tenantId || null;

    const value = {
        currentUser,
        userProfile,
        role,
        linkedStudentIds,
        tenantId,
        authReady,
        profileLoading,
        login,
        // Phase 1.9 contract: Google OAuth and phone OTP are out of Phase 1
        // scope. Surfaces a clear error if a UI tries to use them under the
        // api backend (the firebase backend keeps working as before).
        loginWithGoogle: notImplemented('loginWithGoogle'),
        register,
        logout,
        resetPassword,
        startPhoneSignIn: notImplemented('startPhoneSignIn'),
        confirmPhoneSignIn: notImplemented('confirmPhoneSignIn'),
        refreshProfile,
        ROLES
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
